import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface User {
  id: string;
  email: string;
  subscription_tier: string;
  credits_remaining: number;
  api_key?: string;
  referral_code?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, referralCode?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('progeodata_token');
    const storedUser = localStorage.getItem('progeodata_user');
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch { /* ignore */ }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = (await res.json()) as { error?: string };
      throw new Error(err.error || 'Login failed');
    }
    const data = (await res.json()) as { access_token: string; user: User; refresh_token?: string };
    setToken(data.access_token);
    setUser(data.user);
    localStorage.setItem('progeodata_token', data.access_token);
    localStorage.setItem('progeodata_user', JSON.stringify(data.user));
    if (data.user?.api_key) localStorage.setItem('progeodata_api_key', data.user.api_key);
    if (data.refresh_token) localStorage.setItem('progeodata_refresh_token', data.refresh_token);
  }, []);

  const register = useCallback(async (email: string, password: string, referralCode?: string) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, referralCode }),
    });
    if (!res.ok) {
      const err = (await res.json()) as { error?: string };
      throw new Error(err.error || 'Registration failed');
    }
    const data = (await res.json()) as { access_token: string; user: User };
    setToken(data.access_token);
    setUser(data.user);
    localStorage.setItem('progeodata_token', data.access_token);
    localStorage.setItem('progeodata_user', JSON.stringify(data.user));
    if (data.user?.api_key) localStorage.setItem('progeodata_api_key', data.user.api_key);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('progeodata_token');
    localStorage.removeItem('progeodata_user');
    localStorage.removeItem('progeodata_api_key');
    localStorage.removeItem('progeodata_refresh_token');
    window.location.href = '/';
  }, []);

  const refreshUser = useCallback(async () => {
    const currentToken = token || localStorage.getItem('progeodata_token');
    if (!currentToken) return;
    try {
      const res = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${currentToken}` },
      });
      if (res.ok) {
        const data = (await res.json()) as { user: User };
        setUser(data.user);
        localStorage.setItem('progeodata_user', JSON.stringify(data.user));
      }
    } catch { /* ignore */ }
  }, [token]);

  return (
    <AuthContext.Provider value={{
      user, token, isLoading,
      isAuthenticated: !!user && !!token,
      login, register, logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
